/**
 * Export CELÉHO React Flow grafu (všetkých uzlov) ako obrázok — nie len viditeľnej časti.
 * Vypočíta rozmery všetkých uzlov a vyrenderuje ich do veľkého, čitateľného obrázka.
 */
import { getNodesBounds, getViewportForBounds } from '@xyflow/react';
import { toPng, toSvg } from 'html-to-image';

const PADDING = 40;      // px okraj (v prirodzenej mierke)
const MAX_DIM = 12000;   // strop rozmeru obrázka (limity prehliadača)

function computeTarget(nodes: any[], scale: number) {
  const bounds = getNodesBounds(nodes);
  let width = Math.ceil((bounds.width + PADDING * 2) * scale);
  let height = Math.ceil((bounds.height + PADDING * 2) * scale);
  // zmenšenie ak prekročí strop (zachová pomer)
  const over = Math.max(width / MAX_DIM, height / MAX_DIM, 1);
  width = Math.floor(width / over);
  height = Math.floor(height / over);
  const viewport = getViewportForBounds(bounds, width, height, 0.2, scale, 0.1);
  return { width, height, viewport };
}

function triggerDownload(dataUrl: string, filename: string) {
  const link = document.createElement('a');
  link.download = filename;
  link.href = dataUrl;
  link.click();
}

function getViewportEl(): HTMLElement | null {
  return document.querySelector('.react-flow__viewport') as HTMLElement | null;
}

/** Export celého grafu ako PNG (2× rozlíšenie pre čitateľnosť). */
export async function exportFlowToPng(nodes: any[], filename: string): Promise<void> {
  if (!nodes || nodes.length === 0) { alert('Nie je čo exportovať.'); return; }
  const el = getViewportEl();
  if (!el) { alert('Diagram nie je načítaný.'); return; }
  const { width, height, viewport } = computeTarget(nodes, 2);
  const dataUrl = await toPng(el, {
    backgroundColor: '#ffffff',
    width, height,
    style: {
      width: `${width}px`,
      height: `${height}px`,
      transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`,
    },
    cacheBust: true,
  });
  triggerDownload(dataUrl, filename);
}

/** Export celého grafu ako SVG (vektor — ľubovoľne škálovateľný). */
export async function exportFlowToSvg(nodes: any[], filename: string): Promise<void> {
  if (!nodes || nodes.length === 0) { alert('Nie je čo exportovať.'); return; }
  const el = getViewportEl();
  if (!el) { alert('Diagram nie je načítaný.'); return; }
  const { width, height, viewport } = computeTarget(nodes, 1);
  const dataUrl = await toSvg(el, {
    backgroundColor: '#ffffff',
    width, height,
    style: {
      width: `${width}px`,
      height: `${height}px`,
      transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`,
    },
    cacheBust: true,
  });
  triggerDownload(dataUrl, filename);
}
